import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { KeyRound, ShieldCheck, CheckCircle2, ArrowLeft, Globe, BadgeCheck } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { parseCredentials } from "@/lib/qrpay";
import QrScanner from "@/components/QrScanner";

const SYMBOL = { EUR: "\u20ac", USD: "$" };
const EUR_TO_USD = 1.08;
const convert = (amt, from, to) => {
  if (!amt || from === to) return amt || 0;
  if (from === "EUR" && to === "USD") return amt * EUR_TO_USD;
  if (from === "USD" && to === "EUR") return amt / EUR_TO_USD;
  return amt;
};

// Credentials Pay: paste/scan payment credentials of any scheme, then pay from the card.
// On-net recipient (registered SIGNET merchant) settles in-network; external recipient is
// debited + recorded in the ledger only.
export default function QrPayDialog({ open, onOpenChange, card, onDone, initialPayload }) {
  const [stage, setStage] = useState("enter"); // enter | review | done
  const [parsed, setParsed] = useState(null);
  const [onNet, setOnNet] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [recipCcy, setRecipCcy] = useState("EUR");
  const [amount, setAmount] = useState("");
  const [amountLocked, setAmountLocked] = useState(false);
  const [reference, setReference] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const cardCcy = card?.currency || "USD";

  const reset = () => {
    setStage("enter"); setParsed(null); setOnNet(false);
    setRecipientName(""); setRecipCcy("EUR"); setAmount(""); setAmountLocked(false);
    setReference(""); setPassword(""); setConfirmed(null);
  };

  const loadParsed = async (p) => {
    if (!p || !p.recipient) { toast.error("Could not read those credentials"); return; }
    setParsed(p);
    if (p.amount) { setAmount(String(p.amount)); setAmountLocked(true); } else { setAmountLocked(false); }
    if (p.reference) setReference(p.reference);
    let net = false;
    let name = p.recipient_name || p.recipient;
    let ccy = p.currency || "EUR";
    try {
      const res = await api.get(`/sepa/resolve/${encodeURIComponent(p.recipient)}`);
      net = true; name = res.data.name; ccy = res.data.currency;
    } catch (e) { net = false; }
    setOnNet(net); setRecipientName(name); setRecipCcy(ccy);
    setStage("review");
    toast.success(net ? `SIGNET account: ${name}` : "External recipient loaded");
  };

  useEffect(() => {
    if (!open) return;
    reset();
    if (initialPayload?.recipient) loadParsed(initialPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const debitEstimate = amount ? convert(parseFloat(amount), recipCcy, cardCcy) : 0;

  const pay = async () => {
    if (!amount || !password) { toast.error("Amount and card password are required"); return; }
    setLoading(true);
    try {
      const res = await api.post(`/cards/${card.id}/qr-pay`, {
        password,
        amount: parseFloat(amount),
        recipient: parsed.recipient,
        recipient_name: recipientName,
        currency: recipCcy,
        scheme: parsed.scheme,
        reference: reference || null,
      });
      setConfirmed(res.data);
      setStage("done");
      onDone?.(res.data.new_balance);
      toast.success("Payment signed & sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-[#121216] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#00C2FF]" /> Credentials Pay
          </DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Paste payment credentials (SIGNET, IBAN, EPC/giro, EMVCo or UPI). On-network recipients
            settle instantly; external recipients are recorded in the ledger.
          </DialogDescription>
        </DialogHeader>

        {stage === "enter" && (
          <div className="space-y-3 pt-1">
            <QrScanner onScan={(text) => loadParsed(parseCredentials(text))} onError={() => {}} />
            <p className="text-center text-xs text-[#52525B]">Point your camera at a SIGNET or payment QR to read the credentials</p>
          </div>
        )}

        {stage === "review" && (
          <div className="space-y-4 pt-2" data-testid="qrpay-review">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[#A0A0AB] text-sm">Paying</span>
                <span data-testid="qrpay-recipient" className="font-mono-sig text-white text-right break-all max-w-[60%]">{recipientName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#A0A0AB] text-sm">Route</span>
                <span data-testid="qrpay-route" className={`inline-flex items-center gap-1 font-mono-sig text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full ${onNet ? "bg-[#00FF9D]/15 text-[#00FF9D]" : "bg-[#FFB020]/15 text-[#FFB020]"}`}>
                  {onNet ? <><BadgeCheck className="w-3 h-3" /> On-network</> : <><Globe className="w-3 h-3" /> External · ledger</>}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#A0A0AB] text-sm">Your balance</span>
                <span className="font-mono-sig text-white">{SYMBOL[cardCcy]}{Number(card?.balance || 0).toFixed(2)}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Amount ({recipCcy})</Label>
              <Input
                data-testid="qrpay-amount-input"
                type="number"
                value={amount}
                disabled={amountLocked}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="bg-[#1A1A20] border-white/10 text-white h-11 font-mono-sig disabled:opacity-70"
              />
              {amount && recipCcy !== cardCcy && (
                <p data-testid="qrpay-debit-estimate" className="text-xs text-[#A0A0AB]">
                  Debits ≈ {SYMBOL[cardCcy]}{debitEstimate.toFixed(2)} {cardCcy} from your card
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Card password</Label>
              <Input
                data-testid="qrpay-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Confirm with password"
                className="bg-[#1A1A20] border-white/10 text-white h-11 font-mono-sig"
              />
            </div>
            <div className="flex gap-2">
              <Button data-testid="qrpay-back-button" variant="outline" onClick={() => setStage("enter")} className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10 h-12">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button data-testid="qrpay-submit-button" onClick={pay} disabled={loading} className="flex-1 h-12 bg-[#00C2FF] text-black hover:bg-[#00C2FF]/90 font-semibold rounded-xl">
                <ShieldCheck className="w-4 h-4 mr-2" /> {loading ? "Signing…" : "Sign & pay"}
              </Button>
            </div>
          </div>
        )}

        {stage === "done" && confirmed && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="py-4 text-center space-y-4" data-testid="qrpay-confirmed">
            <CheckCircle2 className="w-14 h-14 text-[#00FF9D] mx-auto" />
            <div>
              <p className="font-display font-black text-2xl">{SYMBOL[confirmed.sent_currency]}{Number(confirmed.sent_amount).toFixed(2)}</p>
              <p className="text-[#A0A0AB] text-sm">paid to {confirmed.wired_to}</p>
              <p className="text-[#A0A0AB] text-xs mt-1">
                Debited {SYMBOL[confirmed.debit_currency]}{Number(confirmed.debit_amount).toFixed(2)} · {confirmed.settlement === "signet" ? "on-network" : "external (ledger)"}
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="font-mono-sig text-[9px] tracking-[0.3em] uppercase text-[#A0A0AB] mb-1">Confirmation signature</p>
              <p data-testid="qrpay-confirmation-code" className="font-mono-sig text-sm tracking-[0.2em] text-[#00F0FF] break-all">{confirmed.transaction?.confirmation_code}</p>
            </div>
            <Button data-testid="qrpay-done-button" onClick={() => { reset(); onOpenChange(false); }} className="w-full h-11 bg-white text-black hover:bg-white/90 rounded-xl font-semibold">Done</Button>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
